/**
 * WebSocket Provider
 * Initializes and manages a single WebSocket connection at the app level
 */

import { useCallback, useEffect, useRef, type ReactNode } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  websocketStatusAtom,
  initializeWebSocketAtom,
  authenticateAtom,
  disconnectWebSocketAtom,
  needsReauthAtom
} from '@/stores/websocket.store';
import { currentUserAtom, accessTokenAtom } from '@/hooks/use-auth';
import { isTokenExpired, refreshTokens } from '@/lib/api';

interface WebSocketProviderProps {
  children: ReactNode;
}

export function WebSocketProvider({ children }: WebSocketProviderProps) {
  const currentUser = useAtomValue(currentUserAtom);
  const accessToken = useAtomValue(accessTokenAtom);
  const status = useAtomValue(websocketStatusAtom);
  const [, initialize] = useAtom(initializeWebSocketAtom);
  const [, authenticate] = useAtom(authenticateAtom);
  const [, disconnect] = useAtom(disconnectWebSocketAtom);
  const needsReauth = useAtomValue(needsReauthAtom);
  const setNeedsReauth = useSetAtom(needsReauthAtom);
  const hasAuthenticatedRef = useRef(false);

  // Refresh the token if expired, then emit the authenticate event
  const performAuth = useCallback(
    async (token: string, accountId: string) => {
      let authToken = token;
      if (isTokenExpired(authToken)) {
        console.log('[WebSocketProvider] Token expired, refreshing before auth...');
        try {
          authToken = await refreshTokens();
        } catch {
          console.error('[WebSocketProvider] Token refresh failed, redirecting to login');
          window.location.href = '/login';
          return;
        }
      }
      console.log('[WebSocketProvider] Authenticating with accountId:', accountId);
      authenticate({ token: authToken, accountId });
    },
    [authenticate]
  );

  // Initialize WebSocket connection once on mount
  useEffect(() => {
    console.log('[WebSocketProvider] Initializing WebSocket...');

    // No breadcrumb for initialization - only track errors
    initialize();

    // Cleanup on unmount
    return () => {
      console.log('[WebSocketProvider] Disconnecting WebSocket...');

      // No breadcrumb for disconnection - only track errors
      hasAuthenticatedRef.current = false;
      disconnect();
    };
  }, [initialize, disconnect]);

  // Authenticate when connected and user is available
  useEffect(() => {
    console.log('[WebSocketProvider] Auth check:', {
      status,
      hasToken: !!accessToken,
      hasUser: !!currentUser,
      accountId: currentUser?.accountId,
      hasAuthenticated: hasAuthenticatedRef.current
    });

    // Only authenticate once when connected and we have credentials
    if (status === 'connected' && accessToken && currentUser?.accountId && !hasAuthenticatedRef.current) {
      hasAuthenticatedRef.current = true;
      void performAuth(accessToken, String(currentUser.accountId));
    }

    // Reset auth flag if disconnected
    if (status === 'disconnected' || status === 'error') {
      hasAuthenticatedRef.current = false;
    }
  }, [status, accessToken, currentUser, performAuth]);

  // Re-authenticate when the server signals the session is no longer valid
  useEffect(() => {
    if (needsReauth && accessToken && currentUser?.accountId) {
      setNeedsReauth(false);
      hasAuthenticatedRef.current = true;
      void performAuth(accessToken, String(currentUser.accountId));
    }
  }, [needsReauth, accessToken, currentUser, performAuth, setNeedsReauth]);

  return <>{children}</>;
}
